/** Angular imports */
import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    OnInit,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { catchError, combineLatest, EMPTY, startWith } from 'rxjs';

/** Core imports */
import { AuthService, DealerService } from '@core';
import { ApiDealerInformation } from '@core/services/dealer/dealer.interface';

/** Component imports */
import { Input } from '@ntv360/component-pantry';

/** Local imports */
import { UiDealersAutocomplete } from '@features/dealers/interfaces';
import { FILL_INFORMATION_STEP_INPUTS } from '@features/playlists/constants';
import { PlaylistDealerAssignment } from '@features/playlists/interfaces';
import { CreatePlaylistStepsService } from '@features/playlists/services/steps/steps.service';
import { AutocompleteComponent, ClientTypeName } from '@shared';
import { Logger, searchHandler, withLoading } from '@shared/utils';

@Component({
    selector: 'app-fill-information',
    imports: [Input, AutocompleteComponent, ReactiveFormsModule],
    templateUrl: './fill-information.component.html',
    styleUrl: './fill-information.component.scss',
})
export class FillInformationComponent implements OnInit {
    /** Service in managing authentication  */
    private authService = inject(AuthService);

    /** Service in managing dealers data and API operations */
    private dealerService = inject(DealerService);

    /** Service in managing create playlist steps and holds the in-memory data */
    private stepsService = inject(CreatePlaylistStepsService);

    /** Manages automatic cleanup of subscriptions */
    private destroyRef = inject(DestroyRef);

    /** Fill information step inputs constant */
    protected fillInformationStepInputs = FILL_INFORMATION_STEP_INPUTS;

    /** Current logged user role taken from session storage */
    protected clientTypeName = computed(() => {
        return this.authService.user()?.data.clientTypeName;
    });

    protected isLoadingDealers = signal<boolean>(false);

    /** Holds the debounced search key value for searching dealers */
    protected searchKey = signal<string>('');

    /** Select dealer options */
    protected dealerOptions = signal<UiDealersAutocomplete[]>([]);

    /** An observable that allows us to subscribe to the dealerOptions signal */
    private dealerOptions$ = toObservable(this.dealerOptions);

    protected fillInformationForm = new FormGroup({
        playlistName: new FormControl('', [
            Validators.required,
            FillInformationComponent.noWhitespaceValidator,
        ]),
        playlistDescription: new FormControl('', [
            Validators.required,
            FillInformationComponent.noWhitespaceValidator,
        ]),
        assignToDealer: new FormControl<string | null>(null, [Validators.required]),
    });

    /**
     * Gets the error message for a form control
     * @param controlName The name of the control
     * @returns The error message or an empty string
     */
    protected getErrorMessage(controlName: string): string {
        const control = this.fillInformationForm.get(controlName);
        if (control?.touched && control?.invalid) {
            if (control.errors?.['required']) {
                return 'This field is required';
            }
        }
        return '';
    }

    /**
     * Initializes the reactive search handler for dealers.
     *
     * Sets up a listener on the `searchKey` to automatically trigger searches via the `dealerService`.
     * This handler manages the loading state, maps successful search results to the UI,
     * and reloads the default dealer list when the search input is cleared.
     * The subscription is automatically cleaned up using `destroyRef` when the context is destroyed.
     */
    constructor() {
        searchHandler(
            this.searchKey,
            (searchKey) => {
                return this.dealerService
                    .searchDealers(searchKey)
                    .pipe(withLoading(this.isLoadingDealers));
            },
            this.destroyRef,
            {
                next: (data) => {
                    this.mapDataToUi(data.data);
                },
                onEmpty: () => {
                    if (this.clientTypeName() !== ClientTypeName.DEALER) {
                        this.loadDealers();
                    }
                },
            },
            {
                debounceTime: 0,
            },
        );

        /**
         * Effect to disable the Assign to Dealer autocomplete when current dealer info is still being fetched
         * NOTE: This is only for the Dealer end/POV (when the clientType is DEALER)
         */
        effect(() => {
            const clientTypeName = this.clientTypeName();
            const isLoading = this.isLoadingDealers();
            if (clientTypeName === ClientTypeName.DEALER && isLoading) {
                this.fillInformationForm.get('assignToDealer')?.patchValue('Loading...');
                this.fillInformationForm.get('assignToDealer')?.disable();
            }
        });
    }

    ngOnInit(): void {
        const clientTypeName = this.clientTypeName();
        const draft = this.stepsService.playlistDraft().fillInformation;

        this.initializeForm();
        this.subscribeToFormChanges();

        if (!clientTypeName) return;

        if (clientTypeName === ClientTypeName.DEALER) {
            if (draft.assignToDealer) {
                this.fillInformationForm.get('assignToDealer')?.disable();
                return;
            }

            const userId = this.authService.user()?.data.userId || '';

            /** Retrieve delaer info and patch the form and update the draft with dealer data */
            this.dealerService
                .getDealerByUserId(userId)
                .pipe(withLoading(this.isLoadingDealers))
                .subscribe({
                    next: (res) => {
                        const dealer = res.data;
                        const uiDealer: UiDealersAutocomplete = {
                            ...dealer.clientInformation,
                            label: dealer.clientInformation.businessName,
                            value: dealer.userId as string,
                        };

                        // Since API is not fixed on return proper property placement, we need to just check and fallback to the correct
                        // property
                        const businessName =
                            uiDealer.businessName ??
                            dealer.clientInformation?.businessName ??
                            (dealer as PlaylistDealerAssignment).userClientAssignments?.[0]
                                ?.clientInformation?.businessName;

                        const autoSelectedDealer: UiDealersAutocomplete = {
                            ...uiDealer,
                            businessName,
                            label: businessName,
                        };

                        this.fillInformationForm.get('assignToDealer')?.patchValue(businessName);
                        this.fillInformationForm.get('assignToDealer')?.disable();

                        // Ensure steps service is aware of this "auto-selection"
                        this.stepsService.updateDraft('fillInformation', {
                            ...this.stepsService.playlistDraft().fillInformation,
                            assignToDealer: autoSelectedDealer,
                        });
                    },
                });
        }
    }

    /**
     * Initializes the form with draft data from the steps service
     */
    private initializeForm(): void {
        const draft = this.stepsService.playlistDraft().fillInformation;

        // Extract the string value for the form if it's an object in the draft
        const assignToDealerValue =
            typeof draft.assignToDealer === 'object' && draft.assignToDealer !== null
                ? ((draft.assignToDealer as PlaylistDealerAssignment)?.clientInformation
                      ?.businessName ??
                  (draft.assignToDealer as PlaylistDealerAssignment)?.businessName ??
                  null)
                : (draft.assignToDealer as string | null);

        this.fillInformationForm.patchValue(
            {
                ...draft,
                assignToDealer: assignToDealerValue,
            },
            { emitEvent: false },
        );
    }

    /**
     * Subscribes to form changes and updates the draft in the steps service
     */
    private subscribeToFormChanges(): void {
        const formValue$ = this.fillInformationForm.valueChanges.pipe(
            startWith(this.fillInformationForm.getRawValue()),
        );
        const options$ = this.dealerOptions$.pipe(startWith(this.dealerOptions()));

        combineLatest([formValue$, options$])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const value = this.fillInformationForm.getRawValue();
                const options = this.dealerOptions();
                const currentDraft = untracked(
                    () => this.stepsService.playlistDraft().fillInformation,
                );

                let resolvedDealer:
                    | UiDealersAutocomplete
                    | PlaylistDealerAssignment
                    | null
                    | string = value.assignToDealer;

                if (value.assignToDealer) {
                    // Attempt to find the dealer object in the options list
                    const dealerFromOptions = options.find(
                        (d) => d.ownerId === value.assignToDealer,
                    );

                    if (dealerFromOptions) {
                        resolvedDealer = dealerFromOptions;
                    } else if (
                        typeof currentDraft.assignToDealer === 'object' &&
                        currentDraft.assignToDealer !== null
                    ) {
                        // If options aren't loaded or not found, but we have a matching object in the draft, preserve it
                        const currentDealer =
                            currentDraft.assignToDealer as PlaylistDealerAssignment;
                        if (currentDealer.businessName === value.assignToDealer) {
                            resolvedDealer = currentDealer;
                        }
                    }
                }

                this.stepsService.updateDraft('fillInformation', {
                    ...value,
                    assignToDealer: resolvedDealer,
                });
            });
    }

    /**
     * Validator that requires a control's value to contain more than just whitespace.
     */
    public static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { required: true };
    }

    /** Maps API response to UI requirements */
    private mapDataToUi(dealers: ApiDealerInformation[]): void {
        this.dealerOptions.set(
            dealers.map((d) => ({
                ...d.clientInformation,
                label: d.clientInformation.businessName,
                value: d.clientInformation.ownerId,
            })),
        );
    }

    /** ============================= API Calls ============================= */

    /** Loads initial list of dealers for the select dealer dropdown.
     *  Limits to 20 since it basically supports searching
     */
    private loadDealers(): void {
        if (this.isLoadingDealers()) return;

        this.dealerService
            .getAllReplacement()
            .pipe(
                withLoading(this.isLoadingDealers),
                catchError((err) => {
                    Logger.error('CreatePlaylist', 'error loading dealers', err);
                    return EMPTY;
                }),
            )
            .subscribe((res) => {
                console.log(res.data);
                this.mapDataToUi(res.data);
            });
    }
}
